import { AdServerStroeerSSPSettings } from '@mbrtargeting/metatag-config-types';
import { AdSize } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { loadScript } from '@mbrtargeting/metatag-utils';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverSsspSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { CONFIG_RESOLVER } from '../../../loader/token.js';
import { isAdSizeIdentical } from '../../../utils/adsize-helper.js';
import { LifecycleAction } from '../adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { CreativeSource, CreativeSourceLoadSlotsParams } from '../adslot-controller/creative-source.js';

const log = logger.logGroup({ prefix: 'StroeerSSP' });

const qsParams = (qs: string): Record<string, string> => Object.fromEntries(qs.split('&').map(pair => pair.split('=').map(decodeURIComponent)));

@injectionTarget()
export class StroeerSsp implements CreativeSource {

    @inject(CONFIG_RESOLVER) configResolver!: ConfigResolver;

    private config: AdServerStroeerSSPSettings;

    constructor() {
        this.config = this.configResolver.get(selectAdserverSsspSettings()) || {};
    }

    public async loadSlots({ adslots, done }: CreativeSourceLoadSlotsParams): Promise<void> {
        const parseParams = () => {
            const qs = qsParams(document.location.search.substring(1));
            return {
                isPreview: qs.isPreview === 'yes',
                paramCreativeId: /^[a-f0-9\-]+$/.test(qs.creativeId) ? qs.creativeId : undefined, // expecting e.g. '5b2b4081-7696-460c-b61c-d88d449c9e6a-daefa033-8251-4791-9c3c-49ed4ad1fa32'
                paramAdslot: /^[a-z0-9_]+$/.test(qs.adSlot) ? qs.adSlot : undefined, // expecting banner, topmobile1, out_of_page
                paramDimensions: qs.dimensions?.match(/^([0-9]+)x([0-9]+)/)?.slice(1)?.map(n => parseInt(n)) as AdSize | undefined, // expecting 728x90
                paramPosition: /^[0-9]+$/.test(qs.position) ? parseInt(qs.position) : undefined, // expecting e.g. 3
                paramSlotID: qs.slotID, // TODO what is this?
                scrollIntoView: qs.scrollIntoView === "1"
            }
        }

        const { active = false, useEndpoint, config: { endpointDev, endpointProd } } = this.config;
        const { isPreview, paramAdslot, paramCreativeId, paramDimensions, paramPosition, paramSlotID, scrollIntoView } = parseParams();

        const filterSlots = (adslots: Adslot[]): Adslot[] => {
            // filter by given adslot param; takes precedence if given this is the only slot that we want to preview the creative on - no matter what.
            if (paramAdslot) return adslots.filter(adslot => adslot.slotName === paramAdslot);
            // filter by given dimension and/or positon param
            if (paramDimensions) {
                const adslotsWithDimensions = adslots.filter(adslot => adslot.getSizes().some(size => isAdSizeIdentical(size, paramDimensions)));
                return paramPosition ? [adslotsWithDimensions[paramPosition - 1]] : adslotsWithDimensions;
            }
            // filter by position if given
            return paramPosition ? [adslots[paramPosition - 1]] : adslots;
        }

        if (isPreview) {
            const adslotsFiltered = filterSlots(adslots);
            log.debug('requesting preview only on specific slots. Original queue is %o, actual list of loading slots is %o!', [adslots.map(adslot => adslot.slotName), adslotsFiltered.map(adslot => adslot.slotName)]);
            let previewScrollDone = false;

            for (const adslot of adslotsFiltered) {

                if (scrollIntoView && !previewScrollDone) {
                    previewScrollDone = true;
                    adslot.slotElementPromise.then(slotElement => {
                        slotElement.scrollIntoView({ block: 'center' })
                        log.debug(`Preview scrollIntoView successful for '${adslot.slotName}'`);
                    });
                }

                loadScript({
                    id: `m6r-metatag-preview:${adslot.slotName}`,
                    src: `${useEndpoint === 'dev' ? endpointDev : endpointProd}${paramCreativeId}/preview`,
                    node: adslot.adserverAnchor,
                    async: false,
                    onload: () => log.info('%s: preview loaded', [adslot.slotName.toUpperCase()]),
                    onerror: () => log.error('%s: error loading preview', [adslot.slotName.toUpperCase()]),
                });
                await adslot.lifecycle.dispatch(LifecycleAction.FILL);
            }
            return done(adslots);
        }

        if (!active) {
            return done(adslots);
        }

        // TODO
        /*
        for (const adslot of adslots) {
            const slotIDScript = document.createElement('script');
            slotIDScript.type = 'text/javascript';
            slotIDScript.innerHTML = `var adscale_slot_id = '${paramSlotID}';`;
            adslot.adserverAnchor.appendChild(slotIDScript);

            loadScript({
                src: 'https://js.adscale.de/getads.js',
                async: false,
                node: adslot.adserverAnchor,
            });
        }
        */
        return done(adslots);
    }
}
